import { Route, Routes, useLocation } from 'react-router-dom';
import { FC, Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { AppContext, useAppContext } from './context/App.context';
import { AxiosResponse } from 'axios';
import { HelmetProvider } from 'react-helmet-async';
import {
  Auth,
  countryISO3ToISO2,
  ErrorSnackbar,
  EventTypes,
  NSpinner,
  sopClient,
  User
} from '@barracuda/shared';
import { Head } from './components/Head/Head';
import { Language } from '@barracuda/shared/interface/language.interface';
import { SUPPORTED_LANGUAGES } from './app.utils';
import { HeaderNavbar } from './components/HeaderNavbar/HeaderNavbar';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { ProtectedLoggedInRoute } from './components/ProtectedLoggedInRoute/ProtectedLoggedInRoute';
import { HotjarTracker } from './components/HotjarTracker/HotjarTracker';
import { SideBarNavigation } from './components/SideBarNavigation/SideBarNavigation';
import { useOneTrust } from './hooks/useOneTrust';
import { Footer } from './components/Footer/Footer';
import { analyticsApi } from '@barracuda/shared/analytics';
import { Box } from '@mui/material';
import { useErrorSnackbar } from './hooks/useErrorSnackbar';
import { useLocalizedNavigate } from './hooks/useLocalizedNavigate';
import { useResetGlobalFloatingAlerts } from './store/useAppStore';
import './i18n';

import { OneTrustBanner } from './components/OneTrust/OneTrustBanner';
import { PageNotFound } from './components/PageNotFound/PageNotFound';
import { Initializer } from './components/Initializer/Initializer';
import { ValidatedEmailToast } from './components/ValidatedEmailToast/ValidatedEmailToast';
import { ResultsSurveyVariant } from './components/ResultsSurvey/ResultsSurvey';
import { PurchaseBlockedType } from './components/PurchaseBlockedPopup/PurchaseBlockedPopup';
import { Faq } from './components/Faq/Faq';
import { HelpCenter } from './components/HelpCenter/HelpCenter';
import { isSignUpV2Enabled } from './utils/feature-flags';

const LoginWrapper = lazy(() => import('./components/LoginWrapper/LoginWrapper'));
const Callback = lazy(() => import('./components/Callback/Callback'));
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const AdminPageWrapper = lazy(() => import('./components/AdminPageWrapper/AdminPageWrapper'));
const TestFormat = lazy(() => import('./components/TestFormat/TestFormat'));
const AcceptTest = lazy(() => import('./components/ForInstitutions/ForInstitutions'));
const Homepage = lazy(() => import('./components/Homepage/Homepage'));
const JoinWrapper = lazy(() => import('./components/JoinWrapper/JoinWrapper'));
const SignUpV2 = lazy(() => import('./components/SignUpV2/SignUpV2'));

const ResultsSurvey = lazy(() => import('./components/ResultsSurvey/ResultsSurvey'));
const ActOnSpecifiedCommercialTransactions = lazy(
  () =>
    import('./components/ActOnSpecifiedCommercialTransactions/ActOnSpecifiedCommercialTransactions')
);
const UnsupportedLanguageRedirect = lazy(
  () => import('./components/UnsupportedLanguageRedirect/UnsupportedLanguageRedirect')
);
const TestNotAvailableForThisDevicePopup = lazy(
  () => import('./components/TestNotAvailableForThisDevicePopup/TestNotAvailableForThisDevicePopup')
);
const TermsOfUse = lazy(() => import('./components/TermsOfUse/TermsOfUse'));
const DesktopCallback = lazy(() => import('./components/DesktopCallback/DesktopCallback'));
const DesktopAutologin = lazy(() => import('./components/DesktopAutologin/DesktopAutologin'));
const MobileCamera = lazy(() => import('./components/MobileCamera/MobileCamera'));
const CartWrapper = lazy(() => import('./components/CartWrapper/CartWrapper'));
const OrderSucceedPage = lazy(() => import('./components/OrderSucceedPage/OrderSucceedPage'));
const Institutions = lazy(() => import('./components/Institutions/Institutions'));
const PurchaseBlockedPopup = lazy(
  () => import('./components/PurchaseBlockedPopup/PurchaseBlockedPopup')
);
const AppNotInstalledPopup = lazy(
  () => import('./components/AppNotInstalledPopup/AppNotInstalledPopup')
);
const PoliciesRules = lazy(() => import('./components/PoliciesRules/PoliciesRules'));
const CompleteProfile = lazy(() => import('./components/CompleteProfile/CompleteProfile'));
const AccountBlocked = lazy(() => import('./components/AccountBlocked/AccountBlocked'));
const ValidateEmail = lazy(() => import('./components/JoinWeb/ValidateEmail'));
const DashboardUploadIdRegula = lazy(
  () => import('./components/DashboardUploadIdRegula/DashboardUploadId')
);
const DashboardUploadIdLegacy = lazy(
  () => import('./components/DashboardUploadId/DashboardUploadId')
);
const ManualVerificationLegacy = lazy(
  () => import('./components/DashboardUploadId/ManualVerification')
);

const detectPathNameLanguage = (): Language => {
  // Removing support for different language in localStorage etc. - only ENG now supported
  // const storedLanguage = localStorage.getItem('language') as Language;
  const prefixesMap = SUPPORTED_LANGUAGES.reduce((prev, current) => {
    return prev.set(`/${current}`, current);
  }, new Map<string, Language>());
  const langKey = Array.from(prefixesMap.keys()).find((item) =>
    location.pathname.toLowerCase().startsWith(item.toLowerCase())
  );
  // Removing support for different language in localStorage etc. - only ENG now supported
  // if (langKey) return prefixesMap.get(langKey) || storedLanguage || SUPPORTED_LANGUAGES[0];
  // return storedLanguage || SUPPORTED_LANGUAGES[0];
  if (langKey) return prefixesMap.get(langKey) || SUPPORTED_LANGUAGES[0];
  return SUPPORTED_LANGUAGES[0];
};

const stripLangPrefix = (pathname: string): string =>
  pathname.replace(/^\/[a-zA-Z]{2,3}(\/|$)/, '/');

const contentOnlyRoutes = [
  'verification',
  'manual-verification',
  'complete-profile',
  'mobile-camera',
  'desktop-callback',
  'desktop-autologin'
];
const pathSegments = location.pathname.split('/').filter(Boolean);
const contentOnly = pathSegments.some((segment) => contentOnlyRoutes.includes(segment));

interface GlobalInterceptorProps {
  onRejected: (value: boolean) => void;
  onVpnBlocked: () => void;
  children?: any;
}

const handleHttpError = (
  error: any,
  onRejected: (value: boolean) => void,
  onVpnBlocked: () => void,
  onAccountBlocked: () => void
) => {
  const status: number | undefined = error?.response?.status;
  const type = error.response?.data?.type;

  switch (status) {
    case 401:
      onRejected(false);
      Auth.removeSopToken();
      break;
    case 403:
      if (type === 'VpnValidationException') {
        onVpnBlocked();
      } else if (type === 'AccountBlockedException') {
        onAccountBlocked();
        Auth.removeSopToken();
      }
      break;
  }

  if (process.env.REACT_APP_MODE !== 'prod') {
    console.log('The response was rejected', error);
  }
  throw error;
};

export const GlobalInterceptor: FC<GlobalInterceptorProps> = ({
  children,
  onRejected,
  onVpnBlocked
}) => {
  const navigate = useLocalizedNavigate();
  const { setUser, setIsLoggedIn } = useAppContext();
  const resetGlobalFloatingAlerts = useResetGlobalFloatingAlerts();

  const handleAccountBlocked = useMemo(() => {
    return () => {
      analyticsApi.userChanged(null);
      setUser(undefined);
      setIsLoggedIn(false);
      resetGlobalFloatingAlerts();
      navigate('/account-blocked');
    };
  }, [navigate, setUser, setIsLoggedIn, resetGlobalFloatingAlerts]);

  useEffect(() => {
    const interceptorId = sopClient.httpProvider.interceptors.response.use(
      (response) => response,
      (error) => handleHttpError(error, onRejected, onVpnBlocked, handleAccountBlocked)
    );

    return () => sopClient.httpProvider.interceptors.response.eject(interceptorId);
  }, [onRejected, onVpnBlocked, handleAccountBlocked]);

  return children;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(!!Auth.getSopToken());
  const [user, setUser] = useState<User>();
  const [eCommerceCountryISO2, setECommerceCountryISO2] = useState<string>();
  const [emailPreviewData, setEmailPreviewData] = useState({ company: '', email: '' });
  const [testTaken, setTestTaken] = useState(false);
  const [language, setLanguage] = useState<Language>(detectPathNameLanguage());
  const [availableTests, setAvailableTests] = useState<number>(0);
  const [resultsSurveyOpen, setResultsSurveyOpen] = useState(false);
  const [resultsSurveyVariant, setResultsSurveyVariant] = useState<ResultsSurveyVariant>();
  const [resultsSurveyLicenseId, setResultsSurveyLicenseId] = useState<string>();
  const [code, setCode] = useState<string | undefined>();
  const [ipCountry, setIpCountry] = useState<string | undefined>();
  const [mobileTestNotSupportedPopupOpened, setMobileTestNotSupportedPopupOpened] = useState(false);
  const [appNotInstalledPopupOpened, setAppNotInstalledPopupOpened] = useState(false);
  const { hasActiveOneTrustGroup, oneTrustGroups } = useOneTrust();
  const [purchaseBlockedOpen, setPurchaseBlockedOpen] = useState(false);
  const [purchaseBlockedReason, setPurchaseBlockedReason] =
    useState<PurchaseBlockedType>(undefined);
  const { errorSnackbar, openErrorSnackbar, closeErrorSnackbar } = useErrorSnackbar();
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const resetGlobalFloatingAlerts = useResetGlobalFloatingAlerts();

  useEffect(() => {
    if (contentOnly) return;

    const unsubscribe = Auth.addTokenUpdatedListener(reloadUser);

    Auth.loadExistingSopToken();

    // refresh loggedIn status, if the token was actually expired
    setIsLoggedIn(!!Auth.getSopToken());

    return () => unsubscribe();
  }, []);

  const afterLogin = async (user: User) => {
    analyticsApi.userChanged(user?.externalId);
    setUser(user);
    setIsLoggedIn(true);
    setECommerceCountryISO2(
      countryISO3ToISO2(
        user.countryOfResidence?.isoCode || (await sopClient.getUserIPCountry()).country
      )
    );
  };

  const afterLogout = async (error?: any) => {
    if (error && error.response?.status !== 401) {
      openErrorSnackbar();
    } else if (user) {
      // only show session expired if we actually HAD logged-in user

      openErrorSnackbar('toast.sessionExpired');
      const redirectUrl = stripLangPrefix(location.pathname) + location.search;
      navigate(`login?redirectUrl=${encodeURIComponent(redirectUrl)}`);
    }

    analyticsApi.userChanged(null);
    setUser(undefined);
    setIsLoggedIn(false);
    resetGlobalFloatingAlerts();
    Auth.removeSopToken();
  };

  const reloadUser = async () => {
    try {
      const response: AxiosResponse<User> = await sopClient.getUserData();
      if (
        (!user && response?.data) ||
        (response && user?.externalId !== response?.data?.externalId)
      ) {
        // from logged-out to logged in OR user changed
        afterLogin(response?.data);
      } else if (user && !response?.data) {
        // from logged-in to logged-out
        afterLogout();
      }
    } catch (error: any) {
      afterLogout(error);
    }
  };

  useEffect(() => {
    if (!isLoggedIn || user) {
      analyticsApi.event(EventTypes.WEBSITE_VISITED);
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    sopClient
      .getUserIPCountry()
      .then(({ country, blocked }) => {
        setIpCountry(country);
        setPurchaseBlockedReason(blocked ? 'location' : undefined);
      })
      .catch(() => {
        console.error('Failed to fetch IP country');
      });
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (user && isLoggedIn && ipCountry) {
      const isoCode = user?.countryOfResidence?.isoCode || ipCountry;
      setECommerceCountryISO2(countryISO3ToISO2(isoCode));
    }
  }, [user, isLoggedIn, ipCountry]);

  const handleRedirectCallback = (loggedIn: boolean) => {
    setIsLoggedIn(loggedIn);

    if (!loggedIn) {
      setUser(undefined);
    }
  };

  const onSurveySubmit = () => {
    setResultsSurveyOpen(false);
    setResultsSurveyVariant(undefined);
  };

  const OTConsentUpdate = () => {
    const performanceCookiesAllowed = hasActiveOneTrustGroup(oneTrustGroups.PERFORMANCE_COOKIES);

    analyticsApi.updateTracking(performanceCookiesAllowed);
  };

  const subRoutes: JSX.Element = (
    <Suspense
      fallback={
        <Box sx={{ width: '100%', height: '100%' }}>
          <NSpinner />
        </Box>
      }
    >
      <Routes>
        <Route path='' element={<Homepage />} />
        <Route path='test-format' element={<TestFormat />} />
        <Route path='policies-rules' element={<PoliciesRules />} />
        <Route path='for-institutions' element={<AcceptTest />} />
        <Route path='for-students/institutions' element={<Institutions />} />
        <Route
          path='act-on-specified-commercial-transactions'
          element={<ActOnSpecifiedCommercialTransactions />}
        />
        <Route path='terms-of-use' element={<TermsOfUse />} />
        <Route path='account-blocked' element={<AccountBlocked />} />
        <Route path={'shares/:code/*'} element={<AdminPageWrapper setCode={setCode} />} />
        <Route path={'shares-mts/:code/*'} element={<AdminPageWrapper setCode={setCode} />} />
        <Route
          path='join'
          element={
            <ProtectedLoggedInRoute>
              {isSignUpV2Enabled() ? <SignUpV2 key='join' /> : <JoinWrapper key='join' />}
            </ProtectedLoggedInRoute>
          }
        />
        <Route
          path='login'
          element={
            <ProtectedLoggedInRoute>
              <LoginWrapper key='login' />
            </ProtectedLoggedInRoute>
          }
        />
        <Route
          path='reset-password'
          element={
            <ProtectedLoggedInRoute>
              <LoginWrapper key='reset-password' />
            </ProtectedLoggedInRoute>
          }
        />
        <Route
          path='validate-email'
          element={
            <ProtectedLoggedInRoute>
              <ValidateEmail />
            </ProtectedLoggedInRoute>
          }
        />
        <Route path='desktop-callback' element={<DesktopCallback />} />
        <Route path='desktop-autologin' element={<DesktopAutologin />} />
        <Route path='callback' element={<Callback />} />
        <Route
          path='dashboard/order'
          element={
            <ProtectedRoute>
              <OrderSucceedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='dashboard/*'
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        {process.env.REACT_APP_USE_REGULA_VERIFICATION === 'true' ? (
          <>
            <Route path='verification/:accessToken' element={<DashboardUploadIdRegula />} />
            <Route path='manual-verification' element={<DashboardUploadIdRegula manual />} />
          </>
        ) : (
          <>
            <Route path='verification/:accessToken' element={<DashboardUploadIdLegacy />} />
            <Route path='manual-verification' element={<ManualVerificationLegacy />} />
          </>
        )}
        <Route path='complete-profile/:accessToken' element={<CompleteProfile />} />
        <Route path='mobile-camera/:accessToken' element={<MobileCamera />} />
        <Route
          path='/cart'
          element={
            <ProtectedRoute>
              <CartWrapper />
            </ProtectedRoute>
          }
        />
        <Route path='faq' element={<Faq />} />
        <Route path='help-center' element={<HelpCenter />} />
        <Route path='*' element={<PageNotFound />} />
      </Routes>
    </Suspense>
  );

  const createRoutes = SUPPORTED_LANGUAGES.map((lang, index) => (
    <Route key={`supported-languages-${index}`} path={`/${lang}/*`} element={subRoutes} />
  ));

  const oneTrustApiKey = process.env.REACT_APP_ONETRUST_API_KEY;
  return (
    <HelmetProvider>
      <HotjarTracker
        siteId={process.env.REACT_APP_HOTJAR_SITE_ID}
        version={process.env.REACT_APP_HOTJAR_VERSION}
      />
      {oneTrustApiKey && <OneTrustBanner apiKey={oneTrustApiKey} consentUpdate={OTConsentUpdate} />}
      <Head title='PTE Express' />
      <AppContext.Provider
        value={{
          isLoggedIn,
          setIsLoggedIn,
          user,
          setUser,
          openErrorSnackbar,
          emailPreviewData,
          setEmailPreviewData,
          testTaken,
          setTestTaken,
          eCommerceCountryISO2,
          setECommerceCountryISO2,
          language,
          setLanguage,
          availableTests,
          setAvailableTests,
          resultsSurveyOpen,
          setResultsSurveyOpen,
          resultsSurveyVariant,
          setResultsSurveyVariant,
          resultsSurveyLicenseId,
          setResultsSurveyLicenseId,
          ipCountry,
          mobileTestNotSupportedPopupOpened,
          setMobileTestNotSupportedPopupOpened,
          purchaseBlockedOpen,
          setPurchaseBlockedOpen,
          purchaseBlockedReason,
          setPurchaseBlockedReason,
          appNotInstalledPopupOpened,
          setAppNotInstalledPopupOpened
        }}
      >
        <GlobalInterceptor
          onRejected={handleRedirectCallback}
          onVpnBlocked={() => setPurchaseBlockedReason('vpn')}
        />
        <Initializer />
        {!contentOnly && <HeaderNavbar code={code} />}
        <SideBarNavigation />
        <Box sx={{ flexGrow: 1 }}>
          <Routes>
            {createRoutes}
            {/* When user want to change language from URL instead of 404 he will be redirected */}
            <Route path='/JPN/*' element={<UnsupportedLanguageRedirect language='JPN' />} />
            <Route path='/KOR/*' element={<UnsupportedLanguageRedirect language='KOR' />} />
            <Route key='page-not-found' path='*' element={<PageNotFound />} />
          </Routes>
        </Box>
        {!contentOnly && <Footer />}
        <ErrorSnackbar error={errorSnackbar} handleClose={closeErrorSnackbar} />
        {resultsSurveyVariant && (
          <ResultsSurvey
            isOpen={resultsSurveyOpen}
            variant={resultsSurveyVariant}
            onSubmitted={onSurveySubmit}
            licenseId={resultsSurveyLicenseId}
          />
        )}
        <TestNotAvailableForThisDevicePopup
          mobileTestNotSupportedPopupOpened={mobileTestNotSupportedPopupOpened}
          setMobileTestNotSupportedPopupOpened={setMobileTestNotSupportedPopupOpened}
        />
        <AppNotInstalledPopup
          appNotInstalledPopupOpened={appNotInstalledPopupOpened}
          setAppNotInstalledPopupOpened={setAppNotInstalledPopupOpened}
        />
        <PurchaseBlockedPopup
          opened={purchaseBlockedOpen}
          type={purchaseBlockedReason}
          onClose={() => setPurchaseBlockedOpen(false)}
          onAction={() => {
            if (purchaseBlockedReason === 'location') {
              const anchor = document.createElement('a');
              anchor.href = process.env.REACT_APP_PTE_LINK ?? '';
              anchor.target = '_blank';
              anchor.click();
            }
            setPurchaseBlockedOpen(false);
          }}
        />
        <ValidatedEmailToast />
      </AppContext.Provider>
    </HelmetProvider>
  );
}

export default App;
